// src/components/guards/GuardsTable.jsx
import React from 'react';
import {
  Phone,
  Mail,
  Calendar,
  Edit,
  Trash2,
  Circle,
  AlertTriangle,
} from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';

/**
 * Parse MySQL DATETIME / DATE string as UTC, then convert to a Date.
 * Works with:
 *  - "2025-11-26 04:24:54"
 *  - "2025-11-26T04:24:54.000Z"
 *  - "2025-11-26"
 */
const parseUtcSqlToIstDate = (value) => {
  if (!value) return null;

  const s = String(value).trim();
  if (!s) return null;

  // If ISO already has timezone info
  const hasTZ = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(s);
  let utcDate;

  if (hasTZ) {
    utcDate = new Date(s);
    if (Number.isNaN(utcDate.getTime())) return null;
  } else {
    const [datePart, timePartRaw = '00:00:00'] = s.split(/[ T]/);
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour = 0, minute = 0, second = 0] = timePartRaw
      .split(':')
      .map((n) => Number(n || 0));

    if (!year || !month || !day) return null;

    const utcMs = Date.UTC(year, month - 1, day, hour, minute, second);
    utcDate = new Date(utcMs);
  }

  return utcDate;
};

// Format as dd/mm/yyyy in IST
const formatISTDate = (value) => {
  const d = parseUtcSqlToIstDate(value);
  if (!d) return '-';

  return d.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const GuardsTable = ({
  guards,
  loading,
  error,
  pagination,
  onPageChange,
  onEdit,
  onDelete,
}) => {
  const { theme } = useTheme();

  const getInitials = (first, last) => {
    const f = first?.[0] || '';
    const l = last?.[0] || '';
    return (f + l).toUpperCase();
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
          <Circle className="h-3 w-3 fill-emerald-500 text-emerald-500" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
        <Circle className="h-3 w-3 text-slate-400" />
        Inactive
      </span>
    );
  };

  // derive per-page size for serial number (fallback to current guards length)
  const perPage = pagination?.limit || guards.length || 1;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-6 sm:py-4">
        <div>
          <h3 className="text-base font-bold text-slate-800 sm:text-lg">
            Guards List
          </h3>
          <p className="mt-1 text-xs text-slate-600 sm:text-sm">
            Overview of all guards and their current status.
          </p>
        </div>
        {loading && (
          <p className="text-xs text-slate-500">Loading guards...</p>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-800 sm:px-6">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600 sm:px-6">
                S.No
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600 sm:px-6">
                Guard
              </th>
              <th className="hidden px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600 sm:table-cell sm:px-6">
                Contact
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600 sm:px-6">
                Username / Code
              </th>
              <th className="hidden px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600 md:table-cell md:px-6">
                Joined On (IST)
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600 sm:px-6">
                Status
              </th>
              <th className="hidden px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600 lg:table-cell lg:px-6">
                Notes
              </th>
              <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600 sm:px-6">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {guards.map((g, index) => {
              const serial =
                (pagination.page - 1) * perPage + index + 1;

              return (
                <tr
                  key={g.id}
                  className="transition-colors hover:bg-slate-50"
                >
                  {/* Serial Number */}
                  <td className="px-4 py-4 text-xs text-slate-700 sm:px-6">
                    {serial}
                  </td>

                  {/* Guard */}
                  <td className="px-4 py-4 sm:px-6">
                    <div className="flex items-center gap-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold text-white shadow-sm"
                        style={{
                          background: `linear-gradient(135deg, ${
                            theme.primary?.[500] || '#0284c7'
                          }, ${theme.secondary?.[500] || '#6366f1'})`,
                        }}
                      >
                        {getInitials(g.first_name, g.last_name)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {g.first_name} {g.last_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          Guard • {g.status === 'active' ? 'On duty' : 'Inactive'}
                        </p>

                        {/* Contact info on mobile */}
                        <div className="mt-2 flex flex-col gap-1 text-xs text-slate-600 sm:hidden">
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-slate-400" />
                            <span>{g.phone}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-slate-400" />
                            <span className="truncate max-w-[180px]">
                              {g.email}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Contact (desktop only) */}
                  <td className="hidden px-4 py-4 text-sm text-slate-600 sm:table-cell sm:px-6">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3 text-slate-400" />
                        <span>{g.phone}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3 text-slate-400" />
                        <span className="truncate max-w-[220px]">
                          {g.email}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Username / Code */}
                  <td className="px-4 py-4 text-sm text-slate-700 sm:px-6">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-slate-500">
                        Username:{' '}
                        <span className="font-medium">{g.username}</span>
                      </span>
                      <span className="inline-flex w-fit items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                        {g.employee_code || '--'}
                      </span>
                    </div>
                  </td>

                  {/* Joined On (md+) – IST */}
                  <td className="hidden px-4 py-4 text-sm text-slate-600 md:table-cell md:px-6">
                    {g.date_of_joining ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-slate-400" />
                        <span>{formatISTDate(g.date_of_joining)}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Not set</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 sm:px-6">
                    {getStatusBadge(g.status)}
                  </td>

                  {/* Notes (lg+) */}
                  <td className="hidden max-w-xs px-4 py-4 text-xs text-slate-600 lg:table-cell lg:px-6">
                    {g.notes || <span className="text-slate-400">-</span>}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4 text-right sm:px-6">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onEdit(g)}
                        className="rounded-lg p-2 transition-all hover:bg-slate-100"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4 text-slate-600" />
                      </button>
                      <button
                        onClick={() => onDelete(g)}
                        className="rounded-lg p-2 transition-all hover:bg-slate-100"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {!loading && guards.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-sm text-slate-500 sm:px-6"
                >
                  No guards found. Try changing filters or add a new guard.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex flex-col gap-2 border-t border-slate-100 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
        <div className="text-xs sm:text-sm">
          Page {pagination.page} of {pagination.totalPages} • Total{' '}
          {pagination.total} guards
        </div>
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <button
            disabled={pagination.page <= 1 || loading}
            onClick={() => onPageChange(Math.max(pagination.page - 1, 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50 hover:bg-slate-50 sm:text-sm"
          >
            Previous
          </button>
          <button
            disabled={
              pagination.page >= pagination.totalPages || loading
            }
            onClick={() =>
              onPageChange(
                Math.min(
                  pagination.page + 1,
                  pagination.totalPages || pagination.page
                )
              )
            }
            className="rounded-lg border border-slate-200 px-3 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50 hover:bg-slate-50 sm:text-sm"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuardsTable;
